/**
 * The Reach: Deepwake's seed universe. 24 systems, 3 regions, one security
 * band each. Positions and celestial layouts are deterministic (seeded PRNG)
 * so every node builds the identical universe from this file.
 *
 * Lore: humanity spread through the Lattice — jump gates left by the Wardens —
 * until the Stilling killed every gate at once. Generations later the gates of
 * one cluster, the Reach, are waking. (Full codex lives in the client.)
 */

export type Security = 'core' | 'verge' | 'deep';

export interface StationDef {
  id: number;
  name: string;
  faction: string;
  x: number;
  y: number;
  z: number;
}

export interface GateDef {
  id: number;
  toSystem: number;
  name: string;
  x: number;
  y: number;
  z: number;
}

export interface PlanetDef {
  id: number;
  name: string;
  kind: 'gas' | 'rock' | 'ice';
  radius: number;
  x: number;
  y: number;
  z: number;
  seed: number;
}

export interface BeltDef {
  id: number;
  name: string;
  x: number;
  y: number;
  z: number;
  ores: Record<string, number>; // ore key -> abundance weight
}

export interface SystemDef {
  id: number;
  name: string;
  region: string;
  security: Security;
  // Map coordinates (light-years, for the star map)
  mx: number;
  my: number;
  sunRadius: number;
  planets: PlanetDef[];
  stations: StationDef[];
  gates: GateDef[];
  belts: BeltDef[];
}

/** mulberry32: tiny deterministic PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// System graph: [name, region, security, links-by-name]
type Row = [string, string, Security, string[]];

// Hearthlight = Provenance Combine core; The Verge = Tidemark frontier;
// The Deep = Unbound space where the best ore sleeps.
const ROWS: Row[] = [
  // Hearthlight (core)
  ['Provenance', 'Hearthlight', 'core', ['Candlewick', 'Slowglass', 'Ledger']],
  ['Candlewick', 'Hearthlight', 'core', ['Provenance', 'Emberline', 'Quietus']],
  ['Slowglass', 'Hearthlight', 'core', ['Provenance', 'Halcyon Shoal', 'Ledger']],
  ['Emberline', 'Hearthlight', 'core', ['Candlewick', 'Vigilkeep']],
  ['Halcyon Shoal', 'Hearthlight', 'core', ['Slowglass', 'Anchorage']],
  ['Ledger', 'Hearthlight', 'core', ['Provenance', 'Slowglass', 'Grey Harbor']],
  ['Vigilkeep', 'Hearthlight', 'core', ['Emberline', 'Lantern Row']],
  ['Quietus', 'Hearthlight', 'core', ['Candlewick']],
  // The Verge
  ['Tidemark', 'The Verge', 'verge', ['Anchorage', 'Saltlight', 'Driftmark']],
  ['Saltlight', 'The Verge', 'verge', ['Tidemark', 'Crossing Deep']],
  ['Anchorage', 'The Verge', 'verge', ['Halcyon Shoal', 'Tidemark', 'Grey Harbor']],
  ['Crossing Deep', 'The Verge', 'verge', ['Saltlight', 'Pale Meridian', 'Stillwake']],
  ['Grey Harbor', 'The Verge', 'verge', ['Ledger', 'Anchorage', 'Lantern Row']],
  ['Lantern Row', 'The Verge', 'verge', ['Vigilkeep', 'Grey Harbor', 'Pale Meridian']],
  ['Driftmark', 'The Verge', 'verge', ['Tidemark', 'Hollow Crown']],
  ['Pale Meridian', 'The Verge', 'verge', ['Crossing Deep', 'Lantern Row', 'Last Signal']],
  // The Deep
  ['Stillwake', 'The Deep', 'deep', ['Crossing Deep', 'Hollow Crown', 'Silent Ferry']],
  ['Hollow Crown', 'The Deep', 'deep', ['Driftmark', 'Stillwake', 'Redshift Grave']],
  ['Last Signal', 'The Deep', 'deep', ['Pale Meridian', 'Terminus Bell']],
  ['Redshift Grave', 'The Deep', 'deep', ['Hollow Crown', 'The Unlit']],
  ['Silent Ferry', 'The Deep', 'deep', ['Stillwake', "Warden's Rest"]],
  ["Warden's Rest", 'The Deep', 'deep', ['Silent Ferry', 'The Unlit']],
  ['The Unlit', 'The Deep', 'deep', ['Redshift Grave', "Warden's Rest", 'Terminus Bell']],
  ['Terminus Bell', 'The Deep', 'deep', ['Last Signal', 'The Unlit']],
];

const STATION_FACTIONS: Record<Security, string[]> = {
  core: ['Provenance Combine', 'The Vigil'],
  verge: ['Tidemark Compact', 'Provenance Combine'],
  deep: ['Unbound'],
};

const STATION_SUFFIXES = [
  'Foundry', 'Anchorage', 'Bastion', 'Exchange', 'Waystation', 'Refuge', 'Yards', 'Beacon',
];

// Ore families (game data lives in ships/modules; weights here set regional scarcity)
const ORE_BY_SECURITY: Record<Security, Record<string, number>> = {
  core: { cindral: 70, veilspar: 30 },
  verge: { cindral: 40, veilspar: 40, tidegold: 20 },
  deep: { veilspar: 30, tidegold: 40, stillore: 30 },
};

const AU_M = 0.1495978707e12;

export function buildUniverse(): SystemDef[] {
  const byName = new Map<string, number>();
  ROWS.forEach((r, i) => byName.set(r[0], 100 + i));

  const systems: SystemDef[] = [];
  for (let i = 0; i < ROWS.length; i++) {
    const [name, region, security, links] = ROWS[i];
    const id = 100 + i;
    const rand = mulberry32(0xd33b + id * 7919);

    // Star-map position: cluster per region, jitter per system
    const regionIdx = region === 'Hearthlight' ? 0 : region === 'The Verge' ? 1 : 2;
    const mx = regionIdx * 42 + rand() * 34;
    const my = (i % 8) * 12 + rand() * 9;

    const ROMAN = ['I', 'II', 'III', 'IV'];
    const planets: PlanetDef[] = [];
    const planetCount = 1 + Math.floor(rand() * 3);
    for (let pi = 0; pi < planetCount; pi++) {
      const kinds: ('gas' | 'rock' | 'ice')[] = ['gas', 'rock', 'ice'];
      const kind = kinds[Math.floor(rand() * 3)];
      planets.push({
        id: id * 1000 + 300 + pi,
        name: `${name} ${ROMAN[pi]}`,
        kind,
        radius: kind === 'gas' ? 25_000_000 + rand() * 40_000_000 : 4_000_000 + rand() * 9_000_000,
        x: (rand() - 0.5) * 2 * AU_M * (0.4 + pi * 0.5),
        y: (rand() - 0.5) * 2 * AU_M * 0.02,
        z: (rand() - 0.5) * 2 * AU_M * (0.4 + pi * 0.5),
        seed: Math.floor(rand() * 1e9),
      });
    }

    const stations: StationDef[] = [];
    const factions = STATION_FACTIONS[security];
    const stationCount = security === 'core' ? 2 : 1;
    const suffixStart = Math.floor(rand() * STATION_SUFFIXES.length);
    for (let s = 0; s < stationCount; s++) {
      const faction = factions[s % factions.length];
      const suffix = STATION_SUFFIXES[(suffixStart + s) % STATION_SUFFIXES.length];
      stations.push({
        id: id * 1000 + 10 + s,
        name: `${name} ${suffix}`,
        faction,
        x: (rand() - 0.5) * 2 * AU_M * 0.6,
        y: (rand() - 0.5) * 2 * AU_M * 0.02,
        z: (rand() - 0.5) * 2 * AU_M * 0.6,
      });
    }

    const gates: GateDef[] = links.map((toName, g) => {
      const to = byName.get(toName);
      if (to === undefined) throw new Error(`Unknown gate target ${toName} from ${name}`);
      return {
        id: id * 1000 + 100 + g,
        toSystem: to,
        name: `Warden Gate (${toName})`,
        x: (rand() - 0.5) * 2 * AU_M * 1.4,
        y: (rand() - 0.5) * 2 * AU_M * 0.05,
        z: (rand() - 0.5) * 2 * AU_M * 1.4,
      };
    });

    const belts: BeltDef[] = [];
    const beltCount = security === 'core' ? 2 : security === 'verge' ? 3 : 4;
    for (let b = 0; b < beltCount; b++) {
      belts.push({
        id: id * 1000 + 200 + b,
        name: `${name} Drift ${b + 1}`,
        x: (rand() - 0.5) * 2 * AU_M * 1.0,
        y: (rand() - 0.5) * 2 * AU_M * 0.03,
        z: (rand() - 0.5) * 2 * AU_M * 1.0,
        ores: ORE_BY_SECURITY[security],
      });
    }

    systems.push({
      id,
      name,
      region,
      security,
      mx,
      my,
      sunRadius: 300_000_000 + rand() * 400_000_000,
      planets,
      stations,
      gates,
      belts,
    });
  }

  // Validate gate symmetry
  const map = new Map(systems.map((s) => [s.id, s]));
  for (const sys of systems) {
    for (const g of sys.gates) {
      const other = map.get(g.toSystem);
      if (!other || !other.gates.some((og) => og.toSystem === sys.id)) {
        throw new Error(`Asymmetric gate ${sys.name} -> ${g.toSystem}`);
      }
    }
  }
  return systems;
}

export const UNIVERSE: SystemDef[] = buildUniverse();
export const SYSTEM_BY_ID = new Map(UNIVERSE.map((s) => [s.id, s]));
